import React, { useRef, useState } from 'react';
import { useSendInviteMutation } from '../../api/userInvite';

type Props = {
  closeModal?: () => void;
};

const InviteSendModal: React.FC<Props> = ({ closeModal }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Collaborator');
  const [mode, setMode] = useState<'form' | 'success'>('form');
  const [sendInvite, { isLoading, isError, reset }] = useSendInviteMutation();
  const [serverError, setServerError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      // A resolved unwrap() means the server returned 2xx — don't also match on
      // the exact response text, or a reworded backend message silently reads
      // as a failed invite.
      await sendInvite({ email: trimmed, role }).unwrap();
      setMode('success');
      setServerError(null);
    } catch (err) {
      const message = (err as any)?.data?.message;
      setServerError(message || 'Failed to send invitation.');
    }
  };
  return (
    <div>
      <h3 className="text-lg font-semibold mb-3">Invite a Team Member</h3>
      {mode === 'success' ? (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200 p-3 rounded">
            Invitation sent to {email}.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button 
              className="btn"
              onClick={() => {
                reset && reset();
                setMode('form');
                setEmail('');
                setRole('Collaborator');
              }}
            >
              Invite more
            </button>
            <button className="btn btn-primary" onClick={closeModal}>Close</button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-1">Email</label>
            <input
              type="email"
              className="input input-bordered w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              ref={emailInputRef}
              required
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Role</label>
            <select className="select select-bordered w-full" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="Collaborator">Collaborator</option>
              <option value="Administrator">Administrator</option>
            </select>
          </div>
          {(isError || serverError) && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 p-3 rounded">
              {serverError || 'Failed to send invitation. Please try again.'}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn" onClick={closeModal}>Cancel</button>
            <button type="submit" disabled={isLoading} className={`btn btn-primary ${isLoading ? 'loading' : ''}`}>Send Invite</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default InviteSendModal;


